/**
 * Automatically keeps the "free sticker sheet" item in sync with the cart:
 * - Adds it once the cart has at least one real (non-sticker) item
 * - Removes it if the cart becomes empty of real items (so it never sits
 *   alone in an otherwise-empty cart, and never reaches checkout alone)
 * - Never touches quantity/remove UI because the sticker line item is
 *   excluded from normal cart rendering — see free-sticker-banner.liquid
 * - Keeps the header <cart-count> badge(s) accurate even when this script
 *   is the one making the change (the theme's own badge-update code only
 *   runs for its own native add/remove actions, not for changes made by
 *   this script's separate /cart/change.js call)
 *
 * Reliability / speed layers:
 *   1. Patching window.fetch to detect ANY call to /cart/add, /cart/change,
 *      /cart/update or /cart/clear — this reacts the instant a cart
 *      mutation happens, regardless of which UI element triggered it
 *      (native Remove link, quantity buttons, this script itself, etc).
 *      This is the primary, fast path.
 *   2. cart:updated / cart:refresh — the theme's own custom events, as a
 *      second layer in case some flow bypasses fetch (unlikely, but cheap
 *      to keep).
 *   3. MutationObserver on document.body — catches DOM changes even if a
 *      future code path doesn't use fetch() at all.
 *   4. A low-frequency poll (every 1.5s) — last-resort backstop only.
 */
(function () {
  var scriptTag = document.currentScript;
  var stickerVariantId = parseInt((scriptTag && scriptTag.dataset.stickerVariantId) || '0', 10);
  if (!stickerVariantId) return;

  var busy = false;
  var cooldown = false;

  function updateCartCountBadges(itemCount) {
    document.querySelectorAll('cart-count').forEach(function (el) {
      el.textContent = itemCount;
      el.setAttribute('aria-label', itemCount + ' items');
      // FIX: the badge is only ever meant to show when the cart is
      // non-empty (see the `hidden` attribute set server-side in
      // cart-drawer.liquid / header-icons.liquid / mobile-dock.liquid).
      // Without toggling it here too, once the badge becomes visible it
      // stays visible forever, showing a stray "0" instead of just the
      // bare icon once the cart empties out.
      if (itemCount > 0) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  function ensureStickerSynced() {
    if (busy || cooldown) return;
    busy = true;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var stickerItem = cart.items.find(function (item) {
          return item.variant_id === stickerVariantId;
        });

        var realItemCount = cart.items.reduce(function (sum, item) {
          return item.variant_id === stickerVariantId ? sum : sum + item.quantity;
        }, 0);

        if (realItemCount === 0) {
          // No real products in the cart — the sticker must never sit
          // there alone, and must never be the only thing at checkout.
          if (stickerItem) {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: stickerItem.key, quantity: 0 }),
            })
              .then(function (r) { return r.json(); })
              .then(function (updatedCart) {
                // The theme's own badge-update code never runs for this
                // request (it only fires for the theme's own native
                // add/remove calls), so update it ourselves here.
                updateCartCountBadges(updatedCart.item_count);
                notifyCartChanged();
              });
          }
          // Sticker already absent — but the badge may still be stale
          // from whatever native action got us to zero real items.
          updateCartCountBadges(cart.item_count);
          return;
        }

        if (!stickerItem) {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: stickerVariantId,
              quantity: 1,
              properties: { _free_sticker: 'true' },
            }),
          })
            .then(function () { return fetch('/cart.js'); })
            .then(function (r) { return r.json(); })
            .then(function (updatedCart) {
              updateCartCountBadges(updatedCart.item_count);
              notifyCartChanged();
            });
        }
      })
      .catch(function (err) {
        console.warn('[free-sticker] sync failed', err);
      })
      .finally(function () {
        busy = false;
        // Short cooldown prevents the fetch/DOM mutation this script's
        // own add/remove action causes from immediately re-triggering
        // itself in a tight loop.
        cooldown = true;
        setTimeout(function () { cooldown = false; }, 400);
      });
  }

  function notifyCartChanged() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }

  function init() {
    ensureStickerSynced();

    // Layer 1 (primary, fastest): hook the network layer directly, so any
    // cart-mutating request — no matter which button/link triggered it —
    // is caught the instant it completes.
    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var result = originalFetch.apply(this, arguments);
      if (
        typeof url === 'string' &&
        (url.indexOf('/cart/add') !== -1 ||
          url.indexOf('/cart/change') !== -1 ||
          url.indexOf('/cart/update') !== -1 ||
          url.indexOf('/cart/clear') !== -1)
      ) {
        result.then(function () { ensureStickerSynced(); }).catch(function () {});
      }
      return result;
    };

    // Layer 2: the theme's own cart-updated events.
    document.addEventListener('cart:updated', ensureStickerSynced);
    document.addEventListener('cart:refresh', ensureStickerSynced);

    // Layer 3: observe the whole document body — survives the theme
    // replacing <cart-items> (or the whole drawer's inner HTML) wholesale
    // rather than editing it in place.
    var bodyObserver = new MutationObserver(ensureStickerSynced);
    bodyObserver.observe(document.body, { childList: true, subtree: true });

    // Layer 4: low-frequency poll as a final backstop, only while the
    // tab is visible.
    setInterval(function () {
      if (document.visibilityState === 'visible') {
        ensureStickerSynced();
      }
    }, 1500);
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
